import { useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { money } from "../format.js";
import {
  Async, Badge, Button, Card, Check, Empty, ErrorPanel, Field, Fields, Input, Modal, Ribbon, Select,
  Table, Textarea, TopBar, Wrap, useAsync, useToast,
} from "../ui.jsx";

export default function Items() {
  const { settings, reloadApp, me } = useApp();
  const canManage = !!me.permissions["items.manage"];
  const state = useAsync(() => api.get("items"), []);
  const [editing, setEditing] = useState(null); // an item, or {} for a new one
  const sym = settings.currency_symbol;

  const refresh = () => {
    state.reload();
    reloadApp(); // saved items feed the invoice/estimate editors
  };

  return (
    <>
      <TopBar title="Items & services">
        {canManage && (
          <Button kind="pri" onClick={() => setEditing({})}>
            ＋ New item
          </Button>
        )}
      </TopBar>

      <Wrap>
        <Ribbon>
          ⛁ Saved items pre-fill description, rate and tax treatment when you build an invoice or estimate.
        </Ribbon>

        <Card className="mt" bare>
          <Async state={state}>
            {(items) => (
              <Table
                rows={items}
                onRowClick={canManage ? (i) => setEditing(i) : undefined}
                empty={<Empty icon="⛁">No saved items yet</Empty>}
                columns={[
                  { key: "name", label: "Name", render: (i) => <b>{i.name}</b> },
                  {
                    key: "description",
                    label: "Description",
                    render: (i) => <span className="sm dim">{i.description || "—"}</span>,
                  },
                  { key: "unit", label: "Unit", render: (i) => <span className="sm dim">per {i.unit}</span> },
                  {
                    key: "rate",
                    label: "Default rate",
                    align: "right",
                    render: (i) =>
                      Number(i.rate || 0) ? (
                        <span className="num">{money(i.rate, sym)}</span>
                      ) : (
                        <span className="muted">Variable</span>
                      ),
                  },
                  {
                    key: "taxable",
                    label: "Taxable",
                    render: (i) =>
                      i.taxable ? (
                        <Badge status="paid" label={settings.tax_label} />
                      ) : (
                        <Badge status="draft" label="Exempt" />
                      ),
                  },
                  {
                    key: "used",
                    label: "Used on",
                    align: "right",
                    render: (i) => (
                      <span className="sm dim num">
                        {Number(i.used_count || 0)} invoice{Number(i.used_count || 0) === 1 ? "" : "s"}
                      </span>
                    ),
                  },
                  { key: "go", label: "", align: "right", render: () => <span className="muted">›</span> },
                ]}
              />
            )}
          </Async>
        </Card>
      </Wrap>

      {editing && (
        <ItemModal item={editing} onClose={() => setEditing(null)} onSaved={refresh} />
      )}
    </>
  );
}

/* -------------------------------------------------------------- item modal */

function ItemModal({ item, onClose, onSaved }) {
  const { settings } = useApp();
  const toast = useToast();
  const isNew = !item.id;
  const [form, setForm] = useState({
    name: item.name || "",
    description: item.description || "",
    rate: item.rate ?? "",
    unit: item.unit || "hour",
    taxable: item.taxable !== false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  async function run(fn, message) {
    setError(null);
    setBusy(true);
    try {
      await fn();
      toast.push(message);
      onSaved();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const save = () => {
    const body = { ...form, rate: Number(form.rate) || 0 };
    return run(
      () => (isNew ? api.post("items", body) : api.put(`items/${item.id}`, body)),
      isNew ? "Item added" : "Item saved"
    );
  };

  const archive = () => run(() => api.del(`items/${item.id}`), "Item archived");

  return (
    <Modal
      title={isNew ? "New item" : "Edit item"}
      width={480}
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <Button kind="dgr" busy={busy} style={{ marginRight: "auto" }} onClick={archive}>
              Archive
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={save}>
            {isNew ? "Add item" : "Save item"}
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}
        <Field label="Name">
          <Input value={form.name} placeholder="Design retainer" onChange={(e) => set("name", e.target.value)} />
        </Field>
        <Field label="Description">
          <Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} />
        </Field>
        <Fields cols={2}>
          <Field label="Default rate" hint="Leave 0 for a variable rate">
            <Input
              className="i right num"
              value={form.rate}
              placeholder="0.00"
              onChange={(e) => set("rate", e.target.value)}
            />
          </Field>
          <Field label="Unit">
            <Select value={form.unit} onChange={(e) => set("unit", e.target.value)}>
              <option value="hour">hour</option>
              <option value="each">each</option>
              <option value="month">month</option>
              <option value="day">day</option>
            </Select>
          </Field>
        </Fields>
        <Check
          label={`Charge ${settings.tax_label} on this item`}
          checked={form.taxable}
          onChange={(v) => set("taxable", v)}
        />
      </Fields>
    </Modal>
  );
}
